// @flow
import * as React from 'react';
import pluralize from 'pluralize';
import Modal from 'react-modal';

import AdminAutoMessageSftpCredentialsConfirmDeleteDialog from 'admin/components/AdminAutoMessageSftpCredentials/AdminAutoMessageSftpCredentialsConfirmDeleteDialog';
import assertions from 'lib/assertions';
import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Columns2 from 'components/Columns2';
import Container from 'components/Container';
import CopyToClipboardField from 'components/CopyToClipboardField';
import ErrorReporter from 'app/components/ErrorReporter';
import Field from 'components/Field';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Label from 'components/Label';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';

import './styles/AdminAutoMessageSftpCredentialsForm.scss';

type Props = {
  loading: boolean,
  sftpCredentials: ?*,
  orgUuid: string,
  onCredentialChange: (name: ?string) => void,
  onClose: () => void,
};

type State = {
  displayName: ?string,
  showDeleteCredentialConfirmationDialog: boolean,
};

class AdminAutoMessageSftpCredentialsForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { sftpCredentials } = props;

    if (!sftpCredentials) {
      this.state = {
        displayName: undefined,
        showDeleteCredentialConfirmationDialog: false,
      };
      return;
    }

    this.state = {
      displayName: sftpCredentials.displayName,
      showDeleteCredentialConfirmationDialog: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.sftpCredentials && this.props.sftpCredentials) {
      this.handleNameChange(this.props.sftpCredentials.displayName);
    }
  }

  toggleDeleteCredentialConfirmationDialog = () => {
    this.setState({
      showDeleteCredentialConfirmationDialog: !this.state.showDeleteCredentialConfirmationDialog,
    });
  };

  handleNameChange = (displayName: ?string) => {
    this.setState({ displayName });
  };

  renderCredentials = (username: string) => {
    return (
      <Row>
        <Text
          align="left"
          element="div"
          className="AdminAutoMessageSftpCredentialsForm-credentials">
          <Text element="p">
            For greater security, your password is only shown once. You can generate new credentials
            from the SFTP credential section of the auto messages tab.
          </Text>
          <Row size="small">
            <Columns2 gutter="small">
              <CopyToClipboardField label="Username" value={username} theme="whiteout" />
              <Field flush>
                <Label>Password</Label>
                <Input
                  className="AdminAutoMessageSftpCredentialsForm-passwordField"
                  value="••••••••••"
                  disabled
                  noBorder
                />
              </Field>
            </Columns2>
          </Row>
          <Button
            onClick={this.toggleDeleteCredentialConfirmationDialog}
            color="apple"
            level="secondary"
            data-test="delete-credentials-button">
            Delete credentials
          </Button>
        </Text>
      </Row>
    );
  };

  renderRelatedAutoMessages = (messages: *) => {
    return (
      <Row>
        <Text align="left">
          Used for{' '}
          <Text weight="semibold">
            {messages.length} {pluralize('message', messages.length)}
          </Text>
          :{' '}
          {messages.map((message, index) => (
            <Text key={message.uuid}>
              <Link href={`auto_messages/${message.uuid}`} target="_blank">
                {message.messageName}
              </Link>
              {index + 1 === messages.length ? null : ', '}
            </Text>
          ))}
        </Text>
      </Row>
    );
  };

  render() {
    const { sftpCredentials, loading, orgUuid } = this.props;
    const { displayName } = this.state;

    const uploadUrl = 'sftp://sftp.remind.com';
    const port = '22';

    if (loading) {
      return (
        <Row>
          <CenteredLoadingIndicator />
        </Row>
      );
    }

    if (!sftpCredentials) {
      return (
        <Container size="small">
          <ErrorReporter error="AdminAutoMessageSftpCredentialsForm - tried to render with an invalid uuid; no sftpCredentials were found" />
          <Row>
            <Text element="p" lineHeight="xlarge">
              Oops! Looks something went wrong. Please close this dialog and either try again or
              select a different set of credentials
            </Text>
            <Button onClick={this.props.onClose}>Close</Button>
          </Row>
        </Container>
      );
    }

    return (
      <React.Fragment>
        <Modal
          isOpen={this.state.showDeleteCredentialConfirmationDialog}
          data-test="delete-credentials-confirm-modal">
          <AdminAutoMessageSftpCredentialsConfirmDeleteDialog
            onRequestHide={this.toggleDeleteCredentialConfirmationDialog}
            username={sftpCredentials.credentials.username}
            orgUuid={orgUuid}
            onConfirm={this.props.onClose}
          />
        </Modal>
        <Container full>
          <Row size="small">
            <FormElement
              data-test="credential-name-field"
              label="Credential name"
              onValidChange={e => this.handleNameChange(e.target.value)}
              onInvalidChange={() => this.handleNameChange(undefined)}
              subtext="The credential name is only visible to you and other administrators. Use it to help you differentiate between sets of credentials."
              validate={e => {
                assertions.notEmpty(e.target.value, 'Please enter a name');
              }}
              element={Input}
              elementProps={{
                defaultValue: displayName,
                placeholder: 'e.g. PowerSchool Attendance, MyLunchMoney Balance',
              }}
              appendedButton={
                <Button
                  disabled={displayName === sftpCredentials.displayName || !displayName}
                  onClick={() => this.props.onCredentialChange(displayName)}>
                  Save
                </Button>
              }
            />
          </Row>
          {sftpCredentials.messages.length > 0 &&
            this.renderRelatedAutoMessages(sftpCredentials.messages)}
          <Row size="small">
            <Columns2 gutter="small">
              <CopyToClipboardField label="Upload URL" value={uploadUrl} />
              <CopyToClipboardField label="Port" value={port} />
            </Columns2>
          </Row>
          {this.renderCredentials(sftpCredentials.credentials.username)}
        </Container>
      </React.Fragment>
    );
  }
}

export default AdminAutoMessageSftpCredentialsForm;
